import { useEffect, useState } from 'react';
import { useDatabase } from '../DatabaseProvider';
import type { TransactionDocType } from '../schemas/transaction.schema';
import { generateId, now } from '../../lib/uuid';
import { useAuthStore } from '../../stores/auth.store';
import { useSyncStore } from '../../stores/sync.store';

export interface TransactionFilters {
  type?: 'income' | 'expense';
  period?: string; // YYYY-MM
  branchId?: string | null;
  paymentMethodId?: string | null;
  categoryId?: string | null;
  searchQuery?: string;
  limit?: number;
  page?: number;
}

export function useTransactions(filters: TransactionFilters) {
  const db = useDatabase();
  const { isOwner, profile, selectedBranchId } = useAuthStore();
  const [transactions, setTransactions] = useState<TransactionDocType[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    const selector: any = { is_deleted: false };
    if (filters.type) {
      selector.type = filters.type;
    }
    if (filters.period) selector.period = filters.period;
    
    // Prioritize explicit filter, fallback to selected store branch, or assigned list
    const activeBranchId = filters.branchId || selectedBranchId;
    if (activeBranchId) {
      selector.branch_id = activeBranchId;
    } else if (!isOwner && profile?.assigned_branches?.length) {
      selector.branch_id = { $in: profile.assigned_branches };
    }
    const pmId = filters.paymentMethodId;
    if (pmId) {
      selector.payments = { $elemMatch: { payment_method_id: pmId } };
    }
    const catId = filters.categoryId;
    if (catId) {
      selector.items = { $elemMatch: { category_id: catId } };
    }

    let query = db.transactions.find({ selector });

    const subscription = query.$.subscribe((docs) => {
      let results = docs.map((d) => d.toJSON()) as TransactionDocType[];
      if (filters.searchQuery) {
        const queryLower = filters.searchQuery.toLowerCase();
        results = results.filter(
          (r) =>
            r.description?.toLowerCase().includes(queryLower) ||
            r.reference?.toLowerCase().includes(queryLower) ||
            r.customer_name?.toLowerCase().includes(queryLower) ||
            r.notes?.toLowerCase().includes(queryLower)
        );
      }
      results.sort((a, b) => b.date.localeCompare(a.date));
      setTotalCount(results.length);

      if (filters.limit && filters.limit > 0) {
        const page = filters.page && filters.page > 0 ? filters.page : 1;
        const startIndex = (page - 1) * filters.limit;
        results = results.slice(startIndex, startIndex + filters.limit);
      }

      setTransactions(results);
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [
    db,
    filters.type,
    filters.period,
    filters.branchId,
    filters.paymentMethodId,
    filters.categoryId,
    filters.searchQuery,
    filters.limit,
    filters.page,
    isOwner,
    profile?.assigned_branches,
    selectedBranchId
  ]);

  return { transactions, totalCount, isLoading };
}

export function useTransactionMutations() {
  const db = useDatabase();
  const user = useAuthStore((s) => s.user);
  const triggerSync = useSyncStore((s) => s.setPendingCount);

  const updatePendingCount = async () => {
    const pendingT = await db.transactions.find({ selector: {  } }).exec();
    triggerSync(pendingT.length);
  };

  const createTransaction = async (
    data: Omit<TransactionDocType, 'id' | 'updated_at' | 'is_deleted' | 'last_modified_by' | 'sync_state'>
  ) => {
    const username = user?.email || 'operador';
    const newDoc: TransactionDocType = {
      ...data,
      id: generateId(),
      updated_at: now(),
      is_deleted: false,
      last_modified_by: username,
      
    };
    await db.transactions.insert(newDoc);
    await updatePendingCount();
    return newDoc;
  };

  const updateTransaction = async (
    id: string,
    data: Partial<Omit<TransactionDocType, 'id' | 'updated_at' | 'is_deleted' | 'last_modified_by' | 'sync_state'>>
  ) => {
    const username = user?.email || 'operador';
    const doc = await db.transactions.findOne(id).exec();
    if (!doc) throw new Error(`Not found`);
    await doc.patch({ ...data, updated_at: now(), last_modified_by: username,  });
    await updatePendingCount();
  };

  const softDeleteTransaction = async (id: string) => {
    const username = user?.email || 'operador';
    const doc = await db.transactions.findOne(id).exec();
    if (!doc) throw new Error(`Not found`);
    await doc.patch({ is_deleted: true, updated_at: now(), last_modified_by: username,  });
    await updatePendingCount();
  };

  return { createTransaction, updateTransaction, softDeleteTransaction };
}
